<script lang="ts" setup>
import { ref } from "vue"
import { downloadEmailAttachment } from "@/services/ProjectService"
import type { EmailAttachment, ReceivedEmail } from "@/services/ProjectService"

interface Props {
    email: ReceivedEmail
}

const props = defineProps<Props>()
defineEmits<{
    (e: "close"): void
}>()

const downloadingId = ref<number | null>(null)

async function download(att: EmailAttachment) {
    downloadingId.value = att.id
    try {
        await downloadEmailAttachment(props.email.testCaseId, props.email.id, att)
    } finally {
        downloadingId.value = null
    }
}

function formatTime(iso: string) {
    return new Date(iso).toLocaleString()
}

function formatSize(bytes?: number) {
    if (!bytes && bytes !== 0) return ""
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
</script>

<template lang="pug">
.modal.show(style="display: block", tabindex="-1")
    .modal-dialog.modal-dialog-centered.modal-lg
        .modal-content
            .modal-header
                h5.modal-title.text-truncate {{ email.subject || "(no subject)" }}
                button.close(type="button", title="Close", @click="$emit('close')") &times;
            .modal-body
                .small.text-muted.mb-2
                    span {{ email.from || "unknown sender" }}
                    span.mx-2 ·
                    span {{ formatTime(email.receivedAt) }}
                //- Untrusted markup in a script-less, network-less sandboxed iframe.
                iframe.email-html(v-if="email.bodyHtml", :srcdoc="email.bodyHtml", sandbox="")
                pre.email-body(v-else-if="email.bodyText") {{ email.bodyText }}
                .text-muted(v-else) (empty body)

                .attachments.mt-3(v-if="email.attachments && email.attachments.length")
                    h6 Attachments
                    .d-flex.flex-wrap
                        button.btn.btn-sm.btn-outline-primary.mr-2.mb-2(
                            v-for="att in email.attachments",
                            :key="att.id",
                            type="button",
                            :disabled="downloadingId === att.id",
                            @click="download(att)"
                        )
                            i.fas.fa-download.mr-1
                            span {{ att.filename || ("attachment-" + att.id) }}
                            span.text-muted.ml-1(v-if="att.sizeBytes") ({{ formatSize(att.sizeBytes) }})
            .modal-footer
                button.btn.btn-secondary(type="button", @click="$emit('close')") Close
    .modal-backdrop.show(style="z-index: -1", @click="$emit('close')")
</template>

<style lang="css" scoped>
.email-body {
    white-space: pre-wrap;
    word-break: break-word;
    max-height: 60vh;
    overflow: auto;
    margin: 0;
}
.email-html {
    width: 100%;
    height: 60vh;
    border: 1px solid var(--light-grey, #ddd);
}
</style>
